import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  lazy,
  Suspense,
  memo,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import {
  SIZE_SCALE,
  SHADOW_SCALE,
  GAP_SCALE,
  COLOR_SCALE,
  MARGIN_TOP,
  PADDING_X,
  PADDING_Y,
  TYPE_SCALE,
  FONT_WEIGHTS,
  RADIUS_SCALE,
  Z_INDEX,
} from '@/components/design-system/tokens';
import PerformanceMonitor from '@/components/dev/PerformanceMonitor';
import { GlossaryProvider } from '@/components/glossary/GlossaryProvider';
import GlobalStyles from '@/components/layout/GlobalStyles';
import { MobileMenu } from '@/components/layout/header/components/MobileMenu';
import Header from '@/components/layout/header/index.tsx';
import MetaTags from '@/components/layout/MetaTags';
import { TranslationProvider } from '@/components/utils/translationHelpers';
import { GOOGLE_TAG_ID } from '@/constants';
import { useFullscreenGallery } from '@/contexts/fullscreenGalleryHooks';
import { ProviderNavigationProvider } from '@/contexts/ProviderNavigationContext';
import { useProviderNavigation } from '@/contexts/providerNavigationHooks';
import { useAccessibility } from '@/hooks/useAccessibility';
import { useAuth, type User } from '@/hooks/useAuth';
import { useHeaderOnLight } from '@/hooks/useHeaderOnLight';
import { useInquiryCount } from '@/hooks/useInquiryCount';
import {
  useProductionWebVitals,
  useProductionPerformanceMetrics,
} from '@/hooks/useProductionWebVitals';
import { useReportCount } from '@/hooks/useReportCount';
import { useTranslation } from '@/hooks/useTranslation';
import { createPageUrl } from '@/utils';
import { initializeGoogleAnalytics } from '@/utils/analytics-utils';
import { logError } from '@/utils/errorLogger';
import { deriveProviderBrandName } from '@/utils/provider/providerHelpers';
import { prefetchLikelyRoutes } from '@/utils/routePrefetch';

// Lazy load components that are not immediately visible or conditionally rendered
const InquiryModal = lazy(() => import('@/components/inquiry/InquiryModal'));
const AccessibilityWidget = lazy(() => import('@/components/accessibility/AccessibilityWidget'));
const ProfileCompletionModal = lazy(() => import('@/components/profile/ProfileCompletionModal'));
const Footer = lazy(() => import('@/components/layout/Footer'));
const SearchBar = lazy(() => import('@/components/layout/SearchBar'));
const FloatingMenu = lazy(() => import('@/components/layout/FloatingMenu'));

// Helper function to create auth handlers
const createAuthHandlers = (
  setUser: React.Dispatch<React.SetStateAction<User | null>>,
  navigate: (path: string, options?: { replace?: boolean; state?: unknown }) => void | Promise<void>
) => {
  const handleLogout = () => {
    const performLogout = async () => {
      try {
        const { signOut } = await import('@/utils/authHelpers');
        await signOut();

        // Clear local state
        setUser(null);

        // Redirect to home to re-initialize state
        window.location.href = createPageUrl('Home');
      } catch (error) {
        logError(error, {
          source: 'Layout:handleLogout',
        });
      }
    };

    void performLogout();
  };

  const handleSignup = () => {
    // Navigate to Auth page with signup mode
    void navigate(createPageUrl('Auth'), {
      state: { mode: 'signup', from: window.location.pathname },
    });
  };

  const handleLogin = () => {
    // Navigate to Auth page with login mode
    void navigate(createPageUrl('Auth'), {
      state: { mode: 'login', from: window.location.pathname },
    });
  };

  return { handleLogout, handleSignup, handleLogin };
};

// Helper function for scroll to top effect
const useScrollToTop = (location: { pathname: string }) => {
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    try {
      window.scrollTo(0, 0);
    } catch (error) {
      // Silently fail scroll errors to prevent layout crashes
      logError(error, {
        source: 'Layout:scrollToTop',
        context: { pathname: location.pathname },
        severity: 'warn',
      });
    }
  }, [location.pathname]);
};

// Helper function for Google Analytics initialization
const useGoogleAnalytics = () => {
  useEffect(() => {
    // Only initialize GA on client side
    if (typeof window === 'undefined') return;

    try {
      // In production, load GA synchronously so WebVitals can use it immediately
      const isProduction = import.meta.env.PROD;
      initializeGoogleAnalytics(GOOGLE_TAG_ID, {
        synchronous: isProduction,
      });
    } catch (error) {
      logError(error, { source: 'Layout:initGoogleAnalytics' });
    }
  }, []);
};

interface Metadata {
  title?: string;
  description?: string;
  image?: string;
}

interface InnerLayoutProps {
  children: React.ReactNode;
  currentPageName: string;
  metadata?: Metadata;
}

interface LayoutProps {
  children: React.ReactNode;
  currentPageName: string;
  metadata?: Metadata;
  language: string;
}

const useLayoutUIState = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [showAccessibility, setShowAccessibility] = useState(false);
  const [showFindMyCareDropdown, setShowFindMyCareDropdown] = useState(false);
  const [headerLanguageDropdownOpen, setHeaderLanguageDropdownOpen] = useState(false);
  const [footerLanguageDropdownOpen, setFooterLanguageDropdownOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const headerLanguageDropdownRef = useRef<HTMLDivElement>(null);
  const footerLanguageDropdownRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  return {
    mobileMenuOpen,
    setMobileMenuOpen,
    showSearch,
    setShowSearch,
    showProfileModal,
    setShowProfileModal,
    showInquiryModal,
    setShowInquiryModal,
    showAccessibility,
    setShowAccessibility,
    showFindMyCareDropdown,
    setShowFindMyCareDropdown,
    headerLanguageDropdownOpen,
    setHeaderLanguageDropdownOpen,
    footerLanguageDropdownOpen,
    setFooterLanguageDropdownOpen,
    expandedSection,
    setExpandedSection,
    userDropdownOpen,
    setUserDropdownOpen,
    headerLanguageDropdownRef,
    footerLanguageDropdownRef,
    userDropdownRef,
  };
};

// Inner Layout component that uses translation context
const InnerLayout: React.FC<InnerLayoutProps> = memo(function InnerLayout({
  children,
  currentPageName,
  metadata: _metadata,
}) {
  // Always call hooks in the same order - never conditionally
  // Error handling is moved to useEffect hooks instead
  const location = useLocation();
  const navigate = useNavigate();

  const { user, setUser } = useAuth();
  const newInquiryCount = useInquiryCount(user);
  const pendingReportCount = useReportCount(user);

  const { currentLanguage, changeLanguage, t } = useTranslation();

  const {
    mobileMenuOpen,
    setMobileMenuOpen,
    showSearch,
    setShowSearch,
    showProfileModal,
    setShowProfileModal,
    showInquiryModal,
    setShowInquiryModal,
    showAccessibility,
    setShowAccessibility,
    showFindMyCareDropdown,
    setShowFindMyCareDropdown,
    headerLanguageDropdownOpen,
    setHeaderLanguageDropdownOpen,
    footerLanguageDropdownOpen,
    setFooterLanguageDropdownOpen,
    expandedSection,
    setExpandedSection,
    userDropdownOpen,
    setUserDropdownOpen,
    headerLanguageDropdownRef,
    footerLanguageDropdownRef,
    userDropdownRef,
  } = useLayoutUIState();

  // Custom hooks for separated concerns
  const { accessibilitySettings, toggleAccessibilitySetting } = useAccessibility();
  const headerOnLight = useHeaderOnLight(currentPageName);

  // Provider navigation context for jump menu
  const { providerNavigation } = useProviderNavigation();

  // Fullscreen gallery context
  const { isGalleryFullscreen, isLogoLightboxOpen } = useFullscreenGallery();

  // Scroll to top on route change - instant to prevent seeing footer
  useScrollToTop(location);

  // Implement Google Analytics - Load synchronously in production for WebVitals
  useGoogleAnalytics();

  // Derive provider name from URL slug for ProviderDetail pages to show in the navbar
  const providerBranding = providerNavigation.branding;

  const providerBrandName = useMemo(() => {
    if (providerBranding.name) {
      return providerBranding.name;
    }
    return deriveProviderBrandName(currentPageName, location.pathname);
  }, [providerBranding.name, currentPageName, location.pathname]);

  // Set HTML lang attribute to prevent Google Translate from auto-translating
  useEffect(() => {
    // Only run on client side to prevent SSR/hydration mismatches
    if (typeof window === 'undefined') return;

    try {
      // Language code to BCP 47 language tag mapping for proper HTML lang attribute
      const langMap = {
        en: 'en',
        ko: 'ko',
        zh: 'zh-CN',
        ja: 'ja',
        vi: 'vi',
        ru: 'ru',
        mn: 'mn',
        ar: 'ar',
        th: 'th',
        id: 'id',
        es: 'es',
      };

      const langTag = langMap[currentLanguage as keyof typeof langMap] || 'en';
      document.documentElement.lang = langTag;
      document.documentElement.setAttribute('translate', 'no');

      // Add notranslate class to body to prevent any remaining translation attempts
      document.body.classList.add('notranslate');
    } catch (error) {
      // Silently fail DOM manipulation errors to prevent layout crashes
      logError(error, {
        source: 'Layout:setHtmlLang',
        context: { currentLanguage },
        severity: 'warn',
      });
    }
  }, [currentLanguage]);

  // Prefetch likely next routes based on current page
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let prefetchId: number | null = null;
    let timeoutId: number | null = null;

    const prefetchRoutes = () => {
      try {
        prefetchLikelyRoutes(location.pathname, currentLanguage);
      } catch (error) {
        logError(error, {
          source: 'Layout:prefetchLikelyRoutes',
          context: {
            path: location.pathname,
            language: currentLanguage,
          },
          severity: 'warn',
        });
      }
    };

    try {
      // Use requestIdleCallback for better performance, fallback to setTimeout
      if (typeof window.requestIdleCallback === 'function') {
        prefetchId = window.requestIdleCallback(prefetchRoutes, {
          timeout: 5000,
        });
      } else {
        timeoutId = window.setTimeout(prefetchRoutes, 100);
      }
    } catch (error) {
      logError(error, {
        source: 'Layout:prefetchSetup',
        context: {
          path: location.pathname,
          language: currentLanguage,
        },
        severity: 'warn',
      });
    }

    return () => {
      try {
        if (prefetchId !== null && typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(prefetchId);
        }
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
      } catch {
        // Silently fail cleanup errors
      }
    };
  }, [location.pathname, currentLanguage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    // Only add event listeners on client side
    if (typeof window === 'undefined' || !userDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };

    try {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    } catch (error) {
      logError(error, {
        source: 'Layout:userDropdownClickOutside',
        context: { userDropdownOpen },
        severity: 'warn',
      });
      return undefined;
    }
  }, [userDropdownOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close header language dropdown when clicking outside
  useEffect(() => {
    // Only add event listeners on client side
    if (typeof window === 'undefined' || !headerLanguageDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        headerLanguageDropdownRef.current &&
        !headerLanguageDropdownRef.current.contains(event.target as Node)
      ) {
        setHeaderLanguageDropdownOpen(false);
      }
    };

    try {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    } catch (error) {
      logError(error, {
        source: 'Layout:headerLanguageDropdownClickOutside',
        context: { headerLanguageDropdownOpen },
        severity: 'warn',
      });
      return undefined;
    }
  }, [headerLanguageDropdownOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close footer language dropdown when clicking outside
  useEffect(() => {
    // Only add event listeners on client side
    if (typeof window === 'undefined' || !footerLanguageDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        footerLanguageDropdownRef.current &&
        !footerLanguageDropdownRef.current.contains(event.target as Node)
      ) {
        setFooterLanguageDropdownOpen(false);
      }
    };

    try {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    } catch (error) {
      logError(error, {
        source: 'Layout:footerLanguageDropdownClickOutside',
        context: { footerLanguageDropdownOpen },
        severity: 'warn',
      });
      return undefined;
    }
  }, [footerLanguageDropdownOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle profile completion modal when user logs in
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined' || !user) return;

    // Check for pending registration
    const pendingRegistration = localStorage.getItem('pending_registration');
    if (pendingRegistration) {
      try {
        const regData = JSON.parse(pendingRegistration) as { registration_attempt?: boolean };
        if (regData.registration_attempt && !user.profile_completed) {
          setShowProfileModal(true);
        }
        localStorage.removeItem('pending_registration');
      } catch (error) {
        logError(error, {
          source: 'Layout:pendingRegistration',
          context: { currentPageName },
        });
        localStorage.removeItem('pending_registration');
      }
    }

    // Show profile modal for incomplete profiles (except on MedipathsAI page)
    if (!user.profile_completed && currentPageName !== 'MedipathsAI') {
      setShowProfileModal(true);
    }
  }, [user, currentPageName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Production performance monitoring
  useProductionWebVitals();
  useProductionPerformanceMetrics();

  const renderHeaderFallback = useCallback(
    ({ resetError, lastErrorAt }: { resetError: () => void; lastErrorAt: string | null }) => (
      <div
        className={`flex flex-col ${GAP_SCALE.xs} border-b ${COLOR_SCALE['border-subtle']} ${COLOR_SCALE.surface} ${PADDING_X['4']} ${PADDING_Y['3']} ${TYPE_SCALE.sm} ${COLOR_SCALE.error} ${SHADOW_SCALE.sm}`}
      >
        <p className={`${FONT_WEIGHTS.medium}`}>
          {t('error_boundary', 'header_unavailable', 'We could not render the navigation.')}
        </p>
        <p className={`${TYPE_SCALE['2xs']} ${COLOR_SCALE.error}`}>
          {t('error_boundary', 'timestamp_label', 'Timestamp')}:{' '}
          {lastErrorAt ?? new Date().toISOString()}
        </p>
        <button
          onClick={resetError}
          className={`self-start ${RADIUS_SCALE.md} border ${COLOR_SCALE['border-subtle']} ${COLOR_SCALE.surface}/80 ${PADDING_X['3']} ${PADDING_Y['1']} ${TYPE_SCALE['2xs']} ${FONT_WEIGHTS.semibold} ${COLOR_SCALE.error} transition hover:${COLOR_SCALE.surface}`}
        >
          {t('error_boundary', 'retry_section', 'Retry section')}
        </button>
      </div>
    ),
    [t]
  );

  // Scroll to top on route change - instant to prevent seeing footer
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    try {
      window.scrollTo(0, 0);
    } catch (error) {
      // Silently fail scroll errors to prevent layout crashes
      logError(error, {
        source: 'Layout:scrollToTop',
        context: { pathname: location.pathname },
        severity: 'warn',
      });
    }
  }, [location.pathname]);

  // Implement Google Analytics - Load synchronously in production for WebVitals
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    try {
      // In production, load GA synchronously so WebVitals can use it immediately
      const isProduction = import.meta.env.PROD;
      initializeGoogleAnalytics(GOOGLE_TAG_ID, {
        synchronous: isProduction,
      });
    } catch (error) {
      logError(error, { source: 'Layout:initGoogleAnalytics' });
    }
  }, []);

  // Implement Google Analytics - Load synchronously in production for WebVitals
  useEffect(() => {
    // Only initialize GA on client side
    if (typeof window === 'undefined') return;

    try {
      // In production, load GA synchronously so WebVitals can use it immediately
      const isProduction = import.meta.env.PROD;
      initializeGoogleAnalytics(GOOGLE_TAG_ID, {
        synchronous: isProduction,
      });
    } catch (error) {
      logError(error, { source: 'Layout:initGoogleAnalytics' });
    }
  }, []);

  // Set HTML lang attribute to prevent Google Translate from auto-translating
  useEffect(() => {
    // Only run on client side to prevent SSR/hydration mismatches
    if (typeof window === 'undefined') return;

    try {
      // Language code to BCP 47 language tag mapping for proper HTML lang attribute
      const langMap = {
        en: 'en',
        ko: 'ko',
        zh: 'zh-CN',
        ja: 'ja',
        vi: 'vi',
        ru: 'ru',
        mn: 'mn',
        ar: 'ar',
        th: 'th',
        id: 'id',
        es: 'es',
      };

      const langTag = langMap[currentLanguage as keyof typeof langMap] || 'en';
      document.documentElement.lang = langTag;
      document.documentElement.setAttribute('translate', 'no');

      // Add notranslate class to body to prevent any remaining translation attempts
      document.body.classList.add('notranslate');
    } catch (error) {
      // Silently fail DOM manipulation errors to prevent layout crashes
      logError(error, {
        source: 'Layout:setHtmlLang',
        context: { currentLanguage },
        severity: 'warn',
      });
    }
  }, [currentLanguage]);

  // Prefetch likely next routes based on current page
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let prefetchId: number | null = null;
    let timeoutId: number | null = null;

    const prefetchRoutes = () => {
      try {
        prefetchLikelyRoutes(location.pathname, currentLanguage);
      } catch (error) {
        logError(error, {
          source: 'Layout:prefetchLikelyRoutes',
          context: {
            path: location.pathname,
            language: currentLanguage,
          },
          severity: 'warn',
        });
      }
    };

    try {
      // Use requestIdleCallback for better performance, fallback to setTimeout
      if (typeof window.requestIdleCallback === 'function') {
        prefetchId = window.requestIdleCallback(prefetchRoutes, {
          timeout: 5000,
        });
      } else {
        // Use longer delay to avoid blocking main thread
        timeoutId = window.setTimeout(prefetchRoutes, 5000);
      }
    } catch (error) {
      logError(error, {
        source: 'Layout:prefetchSetup',
        context: {
          path: location.pathname,
          language: currentLanguage,
        },
        severity: 'warn',
      });
    }

    return () => {
      try {
        if (prefetchId !== null && typeof window.cancelIdleCallback === 'function') {
          window.cancelIdleCallback(prefetchId);
        }
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
      } catch {
        // Silently fail cleanup errors
      }
    };
  }, [location.pathname, currentLanguage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    // Only add event listeners on client side
    if (typeof window === 'undefined' || !userDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };

    try {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    } catch (error) {
      logError(error, {
        source: 'Layout:userDropdownClickOutside',
        context: { userDropdownOpen },
        severity: 'warn',
      });
      return undefined;
    }
  }, [userDropdownOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close header language dropdown when clicking outside
  useEffect(() => {
    // Only add event listeners on client side
    if (typeof window === 'undefined' || !headerLanguageDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        headerLanguageDropdownRef.current &&
        !headerLanguageDropdownRef.current.contains(event.target as Node)
      ) {
        setHeaderLanguageDropdownOpen(false);
      }
    };

    try {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    } catch (error) {
      logError(error, {
        source: 'Layout:headerLanguageDropdownClickOutside',
        context: { headerLanguageDropdownOpen },
        severity: 'warn',
      });
      return undefined;
    }
  }, [headerLanguageDropdownOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close footer language dropdown when clicking outside
  useEffect(() => {
    // Only add event listeners on client side
    if (typeof window === 'undefined' || !footerLanguageDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        footerLanguageDropdownRef.current &&
        !footerLanguageDropdownRef.current.contains(event.target as Node)
      ) {
        setFooterLanguageDropdownOpen(false);
      }
    };

    try {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    } catch (error) {
      logError(error, {
        source: 'Layout:footerLanguageDropdownClickOutside',
        context: { footerLanguageDropdownOpen },
        severity: 'warn',
      });
      return undefined;
    }
  }, [footerLanguageDropdownOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle profile completion modal when user logs in
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined' || !user) return;

    // Check for pending registration
    const pendingRegistration = localStorage.getItem('pending_registration');
    if (pendingRegistration) {
      try {
        const regData = JSON.parse(pendingRegistration) as { registration_attempt?: boolean };
        if (regData.registration_attempt && !user.profile_completed) {
          setShowProfileModal(true);
        }
        localStorage.removeItem('pending_registration');
      } catch (error) {
        logError(error, {
          source: 'Layout:pendingRegistration',
          context: { currentPageName },
        });
        localStorage.removeItem('pending_registration');
      }
    }

    // Show profile modal for incomplete profiles (except on MedipathsAI page)
    if (!user.profile_completed && currentPageName !== 'MedipathsAI') {
      setShowProfileModal(true);
    }
  }, [user, currentPageName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Production performance monitoring
  useProductionWebVitals();
  useProductionPerformanceMetrics();

  const renderFooterFallback = useCallback(
    ({ resetError, lastErrorAt }: { resetError: () => void; lastErrorAt: string | null }) => (
      <div
        className={`w-full ${COLOR_SCALE['surface-muted']} ${PADDING_X['4']} ${PADDING_Y['6']} ${TYPE_SCALE.sm} ${COLOR_SCALE.error}`}
      >
        <p className={FONT_WEIGHTS.medium}>
          {t('error_boundary', 'footer_unavailable', 'Footer content is temporarily unavailable.')}
        </p>
        <p className={`${TYPE_SCALE['2xs']} ${COLOR_SCALE.error}`}>
          {t('error_boundary', 'timestamp_label', 'Timestamp')}:{' '}
          {lastErrorAt ?? new Date().toISOString()}
        </p>
        <button
          onClick={resetError}
          className={`${MARGIN_TOP['2']} ${RADIUS_SCALE.md} border ${COLOR_SCALE['border-subtle']} ${COLOR_SCALE.surface} ${PADDING_X['3']} ${PADDING_Y['1']} ${TYPE_SCALE['2xs']} ${FONT_WEIGHTS.semibold} ${COLOR_SCALE.error} transition hover:${COLOR_SCALE['surface-muted']}`}
        >
          {t('error_boundary', 'retry_section', 'Retry section')}
        </button>
      </div>
    ),
    [t]
  );

  const toggleSection = (sectionId: string) => {
    setExpandedSection(prev => (prev === sectionId ? null : sectionId));
  };

  // Auth handler functions
  const { handleLogout, handleSignup, handleLogin } = createAuthHandlers(setUser, navigate);

  const scrollToTop = () => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      // Silently fail scroll errors to prevent layout crashes
      logError(error, {
        source: 'Layout:scrollToTopSmooth',
        severity: 'warn',
      });
    }
  };

  const shouldShowFloatingMenu = !['BlogPost', 'BlogPostDetail', 'DoctorDetail'].includes(
    currentPageName
  );

  return (
    <SectionErrorBoundary
      source="Layout:Shell"
      fallbackTitle={t('error_boundary', 'layout_shell_error', 'Layout could not render')}
      fallbackMessage={t(
        'error_boundary',
        'layout_shell_message',
        'Please refresh the page to continue.'
      )}
      resetKeys={[currentPageName, currentLanguage]}
    >
      <GlossaryProvider>
        <div className={`flex flex-col ${SIZE_SCALE.screen} ${COLOR_SCALE.surface}`}>
          <GlobalStyles />
          <MetaTags />
          {/* Skip to main content link for accessibility */}
          <a href="#main-content" className="skip-to-main">
            {t('navigation_extended', 'skip_to_content', 'Skip to content')}
          </a>
          {currentPageName !== 'MedipathsAI' && (
            <>
              {/* Mobile Menu Backdrop */}
              {mobileMenuOpen && (
                <div
                  className={`fixed inset-0 ${COLOR_SCALE['dialog-overlay']}`}
                  style={{ zIndex: Z_INDEX.overlay }}
                  onClick={() => setMobileMenuOpen(false)}
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      setMobileMenuOpen(false);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={t('navigation', 'close_menu', 'Close menu')}
                />
              )}

              {!isGalleryFullscreen && !isLogoLightboxOpen && (
                <SectionErrorBoundary
                  source="Layout:Header"
                  renderFallback={renderHeaderFallback}
                  resetKeys={[currentPageName, currentLanguage, headerOnLight]}
                >
                  <Header
                    mobileMenuOpen={mobileMenuOpen}
                    setMobileMenuOpen={setMobileMenuOpen}
                    setShowSearch={setShowSearch}
                    headerOnLight={headerOnLight}
                    currentPageName={currentPageName}
                    {...(providerBrandName && { providerBrandName })}
                    providerBranding={providerBranding}
                    showFindMyCareDropdown={showFindMyCareDropdown}
                    setShowFindMyCareDropdown={setShowFindMyCareDropdown}
                    user={user}
                    newInquiryCount={newInquiryCount}
                    pendingReportCount={pendingReportCount}
                    userDropdownOpen={userDropdownOpen}
                    setUserDropdownOpen={setUserDropdownOpen}
                    userDropdownRef={userDropdownRef}
                    languageDropdownOpen={headerLanguageDropdownOpen}
                    setLanguageDropdownOpen={setHeaderLanguageDropdownOpen}
                    languageDropdownRef={headerLanguageDropdownRef}
                    handleLogout={handleLogout}
                    handleSignup={handleSignup}
                    handleLogin={handleLogin}
                    currentLanguage={currentLanguage}
                    changeLanguage={changeLanguage}
                  />
                </SectionErrorBoundary>
              )}

              {/* Mobile Menu - Rendered outside Header to avoid stacking context issues */}
              {mobileMenuOpen && (
                <SectionErrorBoundary source="Layout:MobileMenu">
                  <MobileMenu
                    mobileMenuOpen={mobileMenuOpen}
                    setMobileMenuOpen={setMobileMenuOpen}
                    scrollToTop={scrollToTop}
                    user={user}
                    newInquiryCount={newInquiryCount}
                    currentLanguage={currentLanguage}
                    changeLanguage={changeLanguage}
                    handleLogout={handleLogout}
                    handleSignup={handleSignup}
                    handleLogin={handleLogin}
                    setShowSearch={setShowSearch}
                    t={t}
                    providerSections={providerNavigation.sections.map(section => ({
                      id: section,
                      label: section,
                    }))}
                    activeSection={providerNavigation.activeSection ?? ''}
                  />
                </SectionErrorBoundary>
              )}

              <SectionErrorBoundary source="Layout:SearchBar">
                <Suspense fallback={null}>
                  <SearchBar
                    showSearch={showSearch}
                    setShowSearch={setShowSearch}
                    scrollToTop={scrollToTop}
                  />
                </Suspense>
              </SectionErrorBoundary>

              {shouldShowFloatingMenu && !isGalleryFullscreen && !isLogoLightboxOpen && (
                <SectionErrorBoundary source="Layout:FloatingMenu">
                  <Suspense fallback={null}>
                    <FloatingMenu />
                  </Suspense>
                </SectionErrorBoundary>
              )}
            </>
          )}

          {showProfileModal && user && (
            <SectionErrorBoundary source="Layout:ProfileCompletionModal">
              <Suspense fallback={null}>
                <ProfileCompletionModal
                  user={user}
                  onClose={() => setShowProfileModal(false)}
                  onComplete={updatedUser => {
                    setUser(prevUser => (prevUser ? { ...prevUser, ...updatedUser } : null));
                    setShowProfileModal(false);
                  }}
                />
              </Suspense>
            </SectionErrorBoundary>
          )}

          {currentPageName === 'MedipathsAI' && (
            <main
              id="main-content"
              className={`flex-1 ${COLOR_SCALE.surface} ${SIZE_SCALE.screen}`}
            >
              {children}
            </main>
          )}

          {currentPageName !== 'MedipathsAI' && (
            <main
              id="main-content"
              className={`${
                currentPageName === 'Home' || currentPageName === 'Auth'
                  ? `flex-1 ${SIZE_SCALE.screen} ${COLOR_SCALE.surface}`
                  : currentPageName === 'DoctorDetail'
                    ? `flex-1 ${COLOR_SCALE['surface-muted']}`
                    : currentPageName === 'ThankYou'
                      ? `flex-1 ${COLOR_SCALE['surface-muted']} mt-20`
                      : `flex-1 ${SIZE_SCALE['16']} ${COLOR_SCALE['surface-muted']}`
              }`}
            >
              {children}
            </main>
          )}

          {/* Inquiry Modal */}
          {showInquiryModal && (
            <SectionErrorBoundary source="Layout:InquiryModal">
              <Suspense fallback={null}>
                <InquiryModal onClose={() => setShowInquiryModal(false)} />
              </Suspense>
            </SectionErrorBoundary>
          )}

          {currentPageName !== 'MedipathsAI' && (
            <SectionErrorBoundary
              source="Layout:Footer"
              renderFallback={renderFooterFallback}
              resetKeys={[currentLanguage, expandedSection]}
            >
              <Suspense
                fallback={<div className={`${SIZE_SCALE['32']} ${COLOR_SCALE['surface-muted']}`} />}
              >
                <Footer
                  expandedSection={expandedSection}
                  toggleSection={toggleSection}
                  scrollToTop={scrollToTop}
                  currentLanguage={currentLanguage}
                  changeLanguage={changeLanguage}
                  languageDropdownOpen={footerLanguageDropdownOpen}
                  setLanguageDropdownOpen={setFooterLanguageDropdownOpen}
                  languageDropdownRef={footerLanguageDropdownRef}
                  accessibilitySettings={accessibilitySettings}
                  toggleAccessibilitySetting={toggleAccessibilitySetting}
                />
              </Suspense>
            </SectionErrorBoundary>
          )}

          <SectionErrorBoundary source="Layout:AccessibilityWidget">
            <Suspense fallback={null}>
              <AccessibilityWidget
                isOpen={showAccessibility}
                onClose={() => setShowAccessibility(false)}
              />
            </Suspense>
          </SectionErrorBoundary>

          {/* Development Performance Monitor */}
          <Suspense fallback={null}>
            {import.meta.env.DEV && (
              <SectionErrorBoundary source="Layout:PerformanceMonitor">
                <PerformanceMonitor />
              </SectionErrorBoundary>
            )}
          </Suspense>
        </div>
      </GlossaryProvider>
    </SectionErrorBoundary>
  );
});

const Layout: React.FC<LayoutProps> = memo(function Layout({
  children,
  currentPageName,
  metadata,
  language,
}) {
  // Redirect HTTP to HTTPS (backup check in layout)
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'http:' &&
      window.location.hostname !== 'localhost'
    ) {
      window.location.href = window.location.href.replace('http:', 'https:');
    }
  }, []);

  return (
    <TranslationProvider initialLanguage={language}>
      <ProviderNavigationProvider>
        <InnerLayout currentPageName={currentPageName} {...(metadata && { metadata })}>
          {children}
        </InnerLayout>
      </ProviderNavigationProvider>
    </TranslationProvider>
  );
});

export default Layout;
